import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, Download, Upload, Search, X, BarChart3, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { getLevelColor, LEVELS } from './utils';
import { useAdminApi, API } from './useAdminApi';

const PAGE_SIZE = 300;

const ThoughtsTab = forwardRef((_, ref) => {
  const { adminApi, adminHeaders } = useAdminApi();

  // Filter / sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterType, setFilterType] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [typesConfig, setTypesConfig] = useState([]);

  // Data state
  const [thoughts, setThoughts] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Add / Edit modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingThought, setEditingThought] = useState(null);
  const [formContent, setFormContent] = useState('');
  const [formLevel, setFormLevel] = useState('spark');
  const [formTypes, setFormTypes] = useState(['team']);

  // Import modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunFile, setDryRunFile] = useState(null);
  const [importPending, setImportPending] = useState(false);

  const fileInputRef = useRef(null);
  const sentinelRef = useRef(null);

  const allTypes = typesConfig.length > 0
    ? typesConfig.map(t => t.key)
    : ['general', 'colleague', 'team', 'friends', 'solo', 'relationship'];

  // ── Debounce search ────────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // ── Initial data (stats + types) ───────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      try {
        const [statsRes, typesRes] = await Promise.all([
          adminApi('get', '/admin/stats'),
          adminApi('get', '/admin/types'),
        ]);
        setStats(statsRes.data);
        setTypesConfig(typesRes.data);
      } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          toast.error('Session expired — please sign in again');
        }
      }
    };
    init();
  }, []);

  // ── Fetch first page when filters change ───────────────────────────────────
  const fetchFirstPage = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams(0, debouncedSearch, filterLevel, filterType, sortBy);
      const res = await adminApi('get', `/admin/thoughts?${params}`);
      const { items, total: t } = res.data;
      setThoughts(items);
      setTotal(t);
      setHasMore(items.length < t);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired — please sign in again');
      }
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filterLevel, filterType, sortBy]);

  useEffect(() => {
    fetchFirstPage();
  }, [fetchFirstPage]);

  // ── Load next page (infinite scroll) ──────────────────────────────────────
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || loading) return;
    setLoadingMore(true);
    try {
      const params = buildParams(thoughts.length, debouncedSearch, filterLevel, filterType, sortBy);
      const res = await adminApi('get', `/admin/thoughts?${params}`);
      const { items, total: t } = res.data;
      setThoughts(prev => [...prev, ...items]);
      setTotal(t);
      setHasMore(thoughts.length + items.length < t);
    } finally {
      setLoadingMore(false);
    }
  }, [thoughts.length, loadingMore, hasMore, loading, debouncedSearch, filterLevel, filterType, sortBy]);

  // ── IntersectionObserver for sentinel ─────────────────────────────────────
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { rootMargin: '300px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  // ── Expose header actions via ref ──────────────────────────────────────────
  const resetForm = () => { setFormContent(''); setFormLevel('spark'); setFormTypes(['team']); };
  const openEditModal = (thought) => {
    setEditingThought(thought);
    setFormContent(thought.content);
    setFormLevel(thought.level);
    setFormTypes(thought.types || [thought.type || 'team']);
  };

  useImperativeHandle(ref, () => ({
    openAdd: () => { resetForm(); setShowAddModal(true); },
    openImport: () => setShowImportModal(true),
    exportCSV: handleExport,
  }));

  // ── CRUD handlers ──────────────────────────────────────────────────────────
  const handleAddThought = async (e) => {
    e.preventDefault();
    try {
      await adminApi('post', '/admin/thoughts', { content: formContent, level: formLevel, types: formTypes });
      toast.success('Thought added!');
      setShowAddModal(false);
      resetForm();
      fetchFirstPage();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add thought');
    }
  };

  const handleUpdateThought = async (e) => {
    e.preventDefault();
    try {
      await adminApi('put', `/admin/thoughts/${editingThought.id}`, { content: formContent, level: formLevel, types: formTypes });
      toast.success('Thought updated!');
      setEditingThought(null);
      resetForm();
      fetchFirstPage();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update thought');
    }
  };

  const handleDeleteThought = async (id) => {
    if (!window.confirm('Delete this thought?')) return;
    try {
      await adminApi('delete', `/admin/thoughts/${id}`);
      toast.success('Thought deleted');
      fetchFirstPage();
    } catch (error) {
      toast.error('Failed to delete thought');
    }
  };

  const handleExport = async () => {
    try {
      const response = await adminApi('get', '/admin/thoughts/export');
      const { csv, count } = response.data;
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `thoughts_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${count} thoughts`);
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  // ── CSV Import ─────────────────────────────────────────────────────────────
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (fileInputRef.current) fileInputRef.current.value = '';
    setDryRunFile(file);
    setImportPending(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios({ method: 'post', url: `${API}/admin/thoughts/dry-run`, data: fd, headers: adminHeaders() });
      setDryRunResult(res.data);
    } catch {
      toast.error('Could not analyse file — check it is a valid CSV');
      setDryRunFile(null);
    } finally {
      setImportPending(false);
    }
  };

  const handleImport = async () => {
    if (!dryRunFile) return;
    setImportPending(true);
    const fd = new FormData();
    fd.append('file', dryRunFile);
    try {
      const res = await axios({ method: 'post', url: `${API}/admin/thoughts/import-csv`, data: fd, headers: adminHeaders() });
      const { imported, skipped, errors } = res.data;
      if (errors?.length) toast.warning(`Imported ${imported}, skipped ${skipped}, ${errors.length} errors`);
      else if (skipped > 0) toast.success(`Imported ${imported} questions (${skipped} duplicates skipped)`);
      else toast.success(`Imported ${imported} questions successfully`);
      fetchFirstPage();
    } catch {
      toast.error('Import failed — please try again');
    } finally {
      setImportPending(false);
      setDryRunResult(null);
      setDryRunFile(null);
      setShowImportModal(false);
    }
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setDryRunResult(null);
    setDryRunFile(null);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="p-4 rounded-xl bg-white/50 border border-charcoal/5">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 size={16} className="text-orange-primary" />
              <span className="text-charcoal/60 text-sm">Total Thoughts</span>
            </div>
            <span className="text-2xl font-serif text-charcoal">{stats.total_thoughts}</span>
          </div>
          <div className="p-4 rounded-xl bg-white/50 border border-charcoal/5">
            <span className="text-charcoal/60 text-sm">Users</span>
            <div className="text-2xl font-serif text-charcoal">{stats.total_users}</div>
          </div>
          <div className="p-4 rounded-xl bg-white/50 border border-charcoal/5">
            <span className="text-charcoal/60 text-sm">Conversations</span>
            <div className="text-2xl font-serif text-charcoal">{stats.total_completed}</div>
          </div>
        </div>
      )}

      {/* Filters + sort */}
      <div className="flex flex-wrap gap-3 mb-6">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40" />
          <input
            type="text"
            placeholder="Search thoughts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-charcoal/10 bg-white/50 focus:outline-none focus:border-orange-primary/50"
            data-testid="search-input"
          />
        </div>

        {/* Level filter */}
        <select
          value={filterLevel}
          onChange={(e) => setFilterLevel(e.target.value)}
          className="px-4 py-2 rounded-lg border border-charcoal/10 bg-white/50 focus:outline-none text-sm"
          data-testid="filter-level"
        >
          <option value="">All Levels</option>
          {LEVELS.map(l => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
        </select>

        {/* Type filter */}
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 rounded-lg border border-charcoal/10 bg-white/50 focus:outline-none text-sm"
          data-testid="filter-type"
        >
          <option value="">All Types</option>
          {allTypes.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
        </select>

        {/* Sort */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-charcoal/10 bg-white/50">
          <ArrowUpDown size={14} className="text-charcoal/40 flex-shrink-0" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-transparent focus:outline-none text-sm text-charcoal/70"
            data-testid="sort-by"
          >
            <option value="newest">Newest</option>
            <option value="rating_desc">Highest Rated</option>
          </select>
        </div>
      </div>

      {/* Result count */}
      <div className="mb-4 text-sm text-charcoal/50">
        {loading ? 'Loading…' : (
          <>
            Showing <span className="text-charcoal font-medium">{thoughts.length}</span> of{' '}
            <span className="text-charcoal font-medium">{total}</span> thoughts
            {(debouncedSearch || filterLevel || filterType) && (
              <button
                onClick={() => { setSearchQuery(''); setFilterLevel(''); setFilterType(''); setSortBy('newest'); }}
                className="ml-3 text-orange-primary hover:underline text-xs"
                data-testid="clear-filters-btn"
              >
                Clear filters
              </button>
            )}
          </>
        )}
      </div>

      {/* Thoughts List */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 rounded-full border-2 border-orange-primary/30 border-t-orange-primary animate-spin mx-auto" />
          </div>
        ) : thoughts.length === 0 ? (
          <div className="text-center py-12 text-charcoal/60">No thoughts found</div>
        ) : thoughts.map((thought) => (
          <motion.div
            key={thought.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 rounded-xl bg-white/50 border border-charcoal/5 flex items-start gap-4"
            data-testid={`thought-${thought.id}`}
          >
            <div className="flex-1">
              <p className="text-charcoal leading-relaxed">{thought.content}</p>
              <div className="flex flex-wrap gap-1.5 mt-2 items-center">
                <span className={`px-2 py-0.5 rounded text-xs ${getLevelColor(thought.level)}`}>{thought.level}</span>
                {(thought.types || [thought.type || 'team']).map(t => (
                  <span key={t} className="px-2 py-0.5 rounded text-xs bg-charcoal/10 text-charcoal/70">{t}</span>
                ))}
                {/* Always show rating */}
                <span className={`px-2 py-0.5 rounded text-xs ml-1 ${
                  thought.rating_count > 0
                    ? 'bg-orange-primary/10 text-orange-primary'
                    : 'bg-charcoal/5 text-charcoal/30'
                }`} data-testid={`rating-${thought.id}`}>
                  {thought.rating_count > 0
                    ? `${thought.rating_avg}/10 (${thought.rating_count})`
                    : '— /10'}
                </span>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={() => openEditModal(thought)}
                className="p-2 rounded-lg hover:bg-charcoal/5 transition-colors"
                data-testid={`edit-${thought.id}`}
              >
                <Edit2 size={16} className="text-charcoal/60" />
              </button>
              <button
                onClick={() => handleDeleteThought(thought.id)}
                className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                data-testid={`delete-${thought.id}`}
              >
                <Trash2 size={16} className="text-red-500" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Sentinel + load more indicator */}
      <div ref={sentinelRef} className="py-4 text-center">
        {loadingMore && (
          <div className="w-8 h-8 rounded-full border-2 border-orange-primary/30 border-t-orange-primary animate-spin mx-auto" />
        )}
        {!loading && !loadingMore && !hasMore && thoughts.length > 0 && (
          <p className="text-charcoal/30 text-sm">All {total} thoughts loaded</p>
        )}
      </div>

      {/* Add/Edit Thought Modal */}
      <AnimatePresence>
        {(showAddModal || editingThought) && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(44, 36, 23, 0.5)' }}
            onClick={() => { setShowAddModal(false); setEditingThought(null); resetForm(); }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-lg rounded-2xl shadow-2xl p-6"
              style={{ backgroundColor: '#FFF8F0' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif text-xl text-charcoal">{editingThought ? 'Edit Thought' : 'Add New Thought'}</h3>
                <button onClick={() => { setShowAddModal(false); setEditingThought(null); resetForm(); }} className="p-2 rounded-full hover:bg-charcoal/5">
                  <X size={20} className="text-charcoal/60" />
                </button>
              </div>
              <form onSubmit={editingThought ? handleUpdateThought : handleAddThought}>
                <div className="mb-4">
                  <label className="block text-sm text-charcoal/60 mb-2">Question Content</label>
                  <textarea
                    value={formContent}
                    onChange={(e) => setFormContent(e.target.value)}
                    rows={3}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-charcoal/10 bg-white/50 focus:outline-none focus:border-orange-primary/50 resize-none"
                    placeholder="Enter the thought/question..."
                    data-testid="thought-content-input"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm text-charcoal/60 mb-2">Depth Level</label>
                  <select
                    value={formLevel}
                    onChange={(e) => setFormLevel(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-charcoal/10 bg-white/50 focus:outline-none"
                    data-testid="thought-level-select"
                  >
                    {LEVELS.map(l => <option key={l} value={l}>{l.charAt(0).toUpperCase() + l.slice(1)}</option>)}
                  </select>
                </div>
                <div className="mb-6">
                  <label className="block text-sm text-charcoal/60 mb-2">Types (select all that apply)</label>
                  <div className="flex flex-wrap gap-2" data-testid="thought-types-checkboxes">
                    {allTypes.map(t => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setFormTypes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])}
                        className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                          formTypes.includes(t)
                            ? 'border-orange-primary bg-orange-primary/10 text-orange-primary'
                            : 'border-charcoal/10 text-charcoal/50 hover:border-charcoal/20'
                        }`}
                      >
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => { setShowAddModal(false); setEditingThought(null); resetForm(); }}
                    className="flex-1 py-3 rounded-full border border-charcoal/20 text-charcoal hover:bg-charcoal/5">Cancel</button>
                  <button type="submit" className="flex-1 py-3 rounded-full text-white" style={{ background: '#ff780a' }}
                    data-testid="save-thought-button">{editingThought ? 'Update' : 'Add'} Thought</button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Import CSV Modal */}
      <AnimatePresence>
        {showImportModal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ backgroundColor: 'rgba(44, 36, 23, 0.5)' }}
            onClick={closeImportModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden"
              style={{ backgroundColor: '#FFF8F0' }}
              onClick={(e) => e.stopPropagation()}
            >
              {!dryRunResult && (
                <div className="p-6">
                  <h3 className="font-serif text-xl text-charcoal mb-1">Import CSV</h3>
                  <p className="text-charcoal/50 text-sm mb-5">Select your file and we'll preview what will change before importing anything.</p>
                  <div className="mb-5 p-4 rounded-xl bg-charcoal/5 font-mono text-xs text-charcoal/60 leading-relaxed">
                    <div className="font-semibold text-charcoal/70 mb-1">Expected columns:</div>
                    "content","level","types"<br />
                    "Your question here","spark","[&quot;team&quot;]"<br />
                    "Another question","depth","[&quot;solo&quot;,&quot;general&quot;]"
                  </div>
                  <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileSelect} className="hidden" data-testid="csv-file-input" />
                  <div className="flex gap-3">
                    <button onClick={closeImportModal} className="flex-1 py-3 rounded-full border border-charcoal/20 text-charcoal hover:bg-charcoal/5 transition-colors">Cancel</button>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={importPending}
                      className="flex-1 py-3 rounded-full text-white flex items-center justify-center gap-2 disabled:opacity-60"
                      style={{ background: '#ff780a' }}
                      data-testid="choose-file-btn"
                    >
                      {importPending ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Upload size={16} />}
                      {importPending ? 'Analysing…' : 'Choose File'}
                    </button>
                  </div>
                </div>
              )}

              {dryRunResult && (
                <div>
                  <div className="px-6 pt-6 pb-4">
                    <h3 className="font-serif text-xl text-charcoal mb-1">Import Preview</h3>
                    <p className="text-charcoal/50 text-sm mb-5">Review what will happen before confirming. Nothing has been saved yet.</p>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      {[
                        { label: 'Will be created', value: dryRunResult.to_create, color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' },
                        { label: 'Duplicates skipped', value: dryRunResult.to_skip, color: 'text-charcoal/60', bg: 'bg-charcoal/5', border: 'border-charcoal/10' },
                        { label: 'Errors', value: dryRunResult.error_count, color: dryRunResult.error_count > 0 ? 'text-red-600' : 'text-charcoal/40', bg: dryRunResult.error_count > 0 ? 'bg-red-50' : 'bg-charcoal/5', border: dryRunResult.error_count > 0 ? 'border-red-200' : 'border-charcoal/10' },
                      ].map(s => (
                        <div key={s.label} className={`rounded-xl p-3 border ${s.bg} ${s.border} text-center`} data-testid={`dryrun-stat-${s.label.split(' ')[0].toLowerCase()}`}>
                          <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                          <div className="text-xs text-charcoal/50 mt-0.5">{s.label}</div>
                        </div>
                      ))}
                    </div>
                    {dryRunResult.to_create > 0 && (
                      <div className="flex gap-2 mb-4 flex-wrap">
                        {Object.entries(dryRunResult.level_breakdown).filter(([, v]) => v > 0).map(([lvl, count]) => (
                          <span key={lvl} className="px-2.5 py-1 rounded-full text-xs font-medium bg-orange-primary/10 text-orange-primary" data-testid={`level-badge-${lvl}`}>
                            {lvl.charAt(0).toUpperCase() + lvl.slice(1)} {count}
                          </span>
                        ))}
                        {dryRunResult.warning_count > 0 && (
                          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-testid="warning-badge">
                            ⚠ {dryRunResult.warning_count} warning{dryRunResult.warning_count !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {(dryRunResult.errors.length > 0 || dryRunResult.warnings.length > 0) && (
                    <div className="max-h-48 overflow-y-auto border-t border-charcoal/8 px-6 py-3 space-y-1.5" data-testid="dryrun-detail-list">
                      {dryRunResult.errors.map((e, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs">
                          <span className="mt-0.5 w-4 h-4 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0 font-bold">!</span>
                          <span className="text-red-700"><span className="font-medium">Row {e.row}:</span> {e.message}</span>
                        </div>
                      ))}
                      {dryRunResult.warnings.map((w, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs">
                          <span className="mt-0.5 w-4 h-4 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0 font-bold">⚠</span>
                          <span className="text-amber-800"><span className="font-medium">Row {w.row}:</span> {w.messages.join('; ')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-3 px-6 pb-6 pt-4 border-t border-charcoal/8">
                    <button
                      onClick={() => { setDryRunResult(null); setDryRunFile(null); }}
                      className="flex-1 py-3 rounded-full border border-charcoal/20 text-charcoal hover:bg-charcoal/5 transition-colors text-sm"
                      data-testid="dryrun-back-btn"
                    >
                      ← Choose different file
                    </button>
                    <button
                      onClick={handleImport}
                      disabled={importPending || dryRunResult.to_create === 0}
                      className="flex-1 py-3 rounded-full text-white flex items-center justify-center gap-2 disabled:opacity-50 text-sm font-medium"
                      style={{ background: '#ff780a' }}
                      data-testid="confirm-import-btn"
                    >
                      {importPending
                        ? <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Importing…</>
                        : `Import ${dryRunResult.to_create} questions`}
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
});

ThoughtsTab.displayName = 'ThoughtsTab';
export default ThoughtsTab;

// ── Helper ────────────────────────────────────────────────────────────────────
function buildParams(skip, search, level, type, sortBy) {
  const p = new URLSearchParams({ skip, limit: PAGE_SIZE, sort_by: sortBy });
  if (search) p.set('search', search);
  if (level) p.set('level', level);
  if (type) p.set('type', type);
  return p.toString();
}
